import { useEffect } from "react";

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string | string[];
  canonical?: string;
  ogImage?: string;
  noindex?: boolean;
}

interface SchemaProps {
  type: string;
  data: Record<string, unknown>;
}

const DEFAULT_TITLE =
  "Selling South FL Waterfront | Luxury Waterfront Homes & Condos 2025";
const DEFAULT_DESCRIPTION =
  "Explore South Florida's premier waterfront properties. Luxury homes, condos, and estates in Miami, Fort Lauderdale, and Boca Raton. Interactive IDX map search with 100+ listings.";
const DEFAULT_KEYWORDS =
  "south florida waterfront real estate, luxury homes miami, fort lauderdale condos, waterfront properties, miami beach oceanfront, boca raton estates, intracoastal homes";
const DEFAULT_IMAGE = "https://sellingsouthflwaterfront.com/og-image.jpg";

const getCanonicalUrl = (explicit?: string) => {
  if (explicit) return explicit;
  if (typeof window === "undefined") {
    return "https://sellingsouthflwaterfront.com/";
  }
  return window.location.href;
};

const ensureMetaTag = (attributes: Record<string, string>) => {
  const selector = Object.entries(attributes)
    .map(([key, value]) => `[${key}="${value}"]`)
    .join("");

  let element = document.head.querySelector(`meta${selector}`) as HTMLMetaElement | null;

  if (!element) {
    element = document.createElement("meta");
    Object.entries(attributes).forEach(([key, value]) => element!.setAttribute(key, value));
    document.head.appendChild(element);
  }

  return element;
};

const ensureLinkTag = (rel: string) => {
  let element = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!element) {
    element = document.createElement("link");
    element.rel = rel;
    document.head.appendChild(element);
  }
  return element;
};

const cleanText = (value: string) => value.replace(/\s+/g, " ").trim();

const arrayFromKeywords = (keywords?: string | string[]) => {
  if (!keywords) return [];
  if (Array.isArray(keywords)) return keywords;
  return keywords
    .split(",")
    .map((keyword) => keyword.trim())
    .filter(Boolean);
};

const SEO = ({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  keywords = DEFAULT_KEYWORDS,
  canonical,
  ogImage = DEFAULT_IMAGE,
  noindex = false,
}: SEOProps) => {
  useEffect(() => {
    if (typeof document === "undefined") return;

    document.title = cleanText(title);

    const descriptionTag = ensureMetaTag({ name: "description" });
    descriptionTag.content = cleanText(description);

    const keywordsTag = ensureMetaTag({ name: "keywords" });
    keywordsTag.content = cleanText(arrayFromKeywords(keywords).join(", "));

    const robotsTag = ensureMetaTag({ name: "robots" });
    robotsTag.content = noindex
      ? "noindex, nofollow"
      : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";

    const canonicalTag = ensureLinkTag("canonical");
    canonicalTag.href = getCanonicalUrl(canonical);

    // Open Graph
    ensureMetaTag({ property: "og:type" }).content = "website";
    ensureMetaTag({ property: "og:title" }).content = cleanText(title);
    ensureMetaTag({ property: "og:description" }).content = cleanText(description);
    ensureMetaTag({ property: "og:url" }).content = getCanonicalUrl(canonical);
    ensureMetaTag({ property: "og:image" }).content = ogImage;
    ensureMetaTag({ property: "og:site_name" }).content = "Selling South FL Waterfront";

    // Twitter
    ensureMetaTag({ property: "twitter:card" }).content = "summary_large_image";
    ensureMetaTag({ property: "twitter:title" }).content = cleanText(title);
    ensureMetaTag({ property: "twitter:description" }).content = cleanText(description);
    ensureMetaTag({ property: "twitter:image" }).content = ogImage;
    ensureMetaTag({ property: "twitter:url" }).content = getCanonicalUrl(canonical);
  }, [title, description, keywords, canonical, ogImage, noindex]);

  return null;
};

export const Schema = ({ type, data }: SchemaProps) => {
  useEffect(() => {
    if (typeof document === "undefined") return;

    const element = document.createElement("script");
    element.type = "application/ld+json";
    element.setAttribute("data-schema", type);
    element.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": type,
      ...data,
    });

    document.head.appendChild(element);

    return () => {
      document.head.removeChild(element);
    };
  }, [type, JSON.stringify(data)]);

  return null;
};

export default SEO;
